
import React, { useRef, useEffect, useState, MouseEvent, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Node, NodeType, SelectionRef, ZoneRect } from '../types';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import { StaticLayer, AgentLayer } from './Canvas/Layers';
import { useAppContext } from '../contexts/AppContext';
import { ICON_METADATA } from './BuildPanel';
import { useCanvasInteractions } from '../hooks/useCanvasInteractions';
import { DEFAULT_NODE_CONFIGS } from '../constants';
import { getZoneCenter } from '../utils/geometry';
import { Bounds, clampSelectionDelta, getCanvasPointerIntent, getRectsBounds, getSelectionBounds, resizeZoneRects, ResizeHandle, selectItemsInRect } from '../utils/editorGeometry';

interface SimulationCanvasProps {
  width: number;
  height: number;
  onBgUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface SimulationCanvasHandle {
    getExportNode: () => HTMLDivElement | null;
    getContentRect: () => { width: number; height: number; x: number; y: number } | null;
}

const createBlueprintNode = (id: string, x: number, y: number, type: NodeType): Node => ({
  id,
  x,
  y,
  type,
  connections: [],
  occupants: [],
  contamination: 0,
  ...DEFAULT_NODE_CONFIGS[type]
});

const distanceToSegment = (point: { x: number; y: number }, start: { x: number; y: number }, end: { x: number; y: number }) => {
  const lengthSquared = Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2);
  if (lengthSquared === 0) return Math.hypot(point.x - start.x, point.y - start.y);
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) / lengthSquared));
  return Math.hypot(point.x - (start.x + t * (end.x - start.x)), point.y - (start.y + t * (end.y - start.y)));
};

type EditorInteraction =
  | { type: 'MOVE'; start: { x: number; y: number }; current: { x: number; y: number }; items: SelectionRef[]; toggleOnClick?: SelectionRef }
  | { type: 'MARQUEE'; start: { x: number; y: number }; current: { x: number; y: number }; additive: boolean }
  | { type: 'RESIZE_ZONE'; start: { x: number; y: number }; current: { x: number; y: number }; zoneId: string; rects: ZoneRect[]; handle: ResizeHandle }
  | { type: 'PATH_POINT'; start: { x: number; y: number }; current: { x: number; y: number }; pathId: string; index: number };

const SimulationCanvas = forwardRef<SimulationCanvasHandle, SimulationCanvasProps>((props, ref) => {
  const { width: worldWidth, height: worldHeight } = props;
  const { state, dispatch } = useAppContext();
  const { 
    nodes, agents, walls, zones, paths, icons, selectedItems, selectedPathPoint, floorPlanImage, mapOpacity, mapBrightness, mapContrast,
    editMode, viewMode, selectedAgentId, pixelsPerMeter, measurementPoints, isControlsVisible
  } = state;
  const selectedPathId = selectedItems.find(item => item.kind === 'PATH')?.id ?? null;
  const selectedNodeIds = new Set(selectedItems.filter(item => item.kind === 'NODE').map(item => item.id));
  const selectedIconIds = new Set(selectedItems.filter(item => item.kind === 'ICON').map(item => item.id));
  const selectedWallIds = new Set(selectedItems.filter(item => item.kind === 'WALL').map(item => item.id));

  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [shiftPressed, setShiftPressed] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [wallStart, setWallStart] = useState<{ x: number; y: number } | null>(null);
  const [editorInteraction, setEditorInteraction] = useState<EditorInteraction | null>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const exportAreaRef = useRef<HTMLDivElement>(null);
  const selectionHandledRef = useRef(false);

  const { cursorPos, setCursorPos, zoneStart, setZoneStart, getMouseWorldPos, snapTarget, handleInteractionStart } = 
    useCanvasInteractions(state, dispatch, exportAreaRef, transformRef);

  const activeWidth = imageSize ? imageSize.width : worldWidth;
  const activeHeight = imageSize ? imageSize.height : worldHeight;
  const measurementMidpoint = measurementPoints.length === 2 ? {
    x: (measurementPoints[0].x + measurementPoints[1].x) / 2,
    y: (measurementPoints[0].y + measurementPoints[1].y) / 2
  } : null;
  const measurementLabel = measurementPoints.length === 2
    ? `${(Math.hypot(measurementPoints[1].x - measurementPoints[0].x, measurementPoints[1].y - measurementPoints[0].y) / pixelsPerMeter).toFixed(2)} m`
    : '';

  const fitToView = useCallback(() => {
    const viewer = viewerRef.current;
    const transform = transformRef.current;
    if (!viewer || !transform || activeWidth <= 0 || activeHeight <= 0) return;
    const rect = viewer.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const leftInset = isControlsVisible ? Math.min(464, rect.width * 0.45) : Math.min(72, rect.width * 0.15);
    const rightInset = Math.min(88, rect.width * 0.15);
    const availableWidth = Math.max(1, rect.width - leftInset - rightInset);
    const scale = Math.max(0.01, Math.min(20, Math.min(availableWidth / activeWidth, rect.height / activeHeight) * 0.94));
    const positionX = leftInset + (availableWidth - activeWidth * scale) / 2;
    const positionY = (rect.height - activeHeight * scale) / 2;
    transform.setTransform(positionX, positionY, scale, 250, 'easeOut');
  }, [activeWidth, activeHeight, isControlsVisible]);

  const zoomBy = useCallback((factor: number) => {
    const viewer = viewerRef.current;
    const transform = transformRef.current;
    if (!viewer || !transform) return;
    const { scale, positionX, positionY } = transform.instance.transformState;
    const nextScale = Math.max(0.01, Math.min(20, scale * factor));
    if (nextScale === scale) return;
    const rect = viewer.getBoundingClientRect();
    const leftInset = isControlsVisible ? Math.min(464, rect.width * 0.45) : Math.min(72, rect.width * 0.15);
    const rightInset = Math.min(88, rect.width * 0.15);
    const centerX = leftInset + (rect.width - leftInset - rightInset) / 2;
    const centerY = rect.height / 2;
    const worldCenterX = (centerX - positionX) / scale;
    const worldCenterY = (centerY - positionY) / scale;
    transform.setTransform(
      centerX - worldCenterX * nextScale,
      centerY - worldCenterY * nextScale,
      nextScale,
      180,
      'easeOut'
    );
  }, [isControlsVisible]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftPressed(e.type === 'keydown');
      if (e.code === 'Space' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        setSpacePressed(e.type === 'keydown');
      }
    };
    const resetKeys = () => { setShiftPressed(false); setSpacePressed(false); };
    window.addEventListener('keydown', handleKey);
    window.addEventListener('keyup', handleKey);
    window.addEventListener('blur', resetKeys);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('keyup', handleKey);
      window.removeEventListener('blur', resetKeys);
    };
  }, []);

  useEffect(() => {
    if (editMode !== 'DRAW_WALL') setWallStart(null);
  }, [editMode]);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(fitToView);
    return () => cancelAnimationFrame(animationFrame);
  }, [fitToView]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const observer = new ResizeObserver(() => fitToView());
    observer.observe(viewer);
    return () => observer.disconnect();
  }, [fitToView]);

  useImperativeHandle(ref, () => ({ 
    getExportNode: () => exportAreaRef.current,
    getContentRect: () => ({ x: 0, y: 0, width: activeWidth, height: activeHeight })
  }));

  useEffect(() => {
    if (floorPlanImage) {
      const img = new Image();
      img.onload = () => { if (img.naturalWidth > 0) setImageSize({ width: img.naturalWidth, height: img.naturalHeight }); };
      img.src = floorPlanImage;
    } else setImageSize(null);
  }, [floorPlanImage]);

  const currentScale = () => transformRef.current?.instance.transformState.scale || 1;

  const findEditableAt = (pos: { x: number; y: number }): SelectionRef | null => {
    const threshold = 12 / currentScale();
    const node = [...nodes].reverse().find(item => Math.hypot(item.x - pos.x, item.y - pos.y) <= threshold);
    if (node) return { kind: 'NODE', id: node.id };
    const icon = [...icons].reverse().find(item => Math.hypot(item.x - pos.x, item.y - pos.y) <= threshold);
    if (icon) return { kind: 'ICON', id: icon.id };
    const wall = [...walls].reverse().find(item => distanceToSegment(pos, { x: item.x1, y: item.y1 }, { x: item.x2, y: item.y2 }) <= threshold);
    if (wall) return { kind: 'WALL', id: wall.id };
    const path = [...paths].reverse().find(item => item.visible && item.points.some((point, index) => index > 0 && distanceToSegment(pos, item.points[index - 1], point) <= threshold));
    if (path) return { kind: 'PATH', id: path.id };
    const zone = [...zones].reverse().find(item => item.rects.some(rect => pos.x >= rect.x && pos.x <= rect.x + rect.w && pos.y >= rect.y && pos.y <= rect.y + rect.h));
    return zone ? { kind: 'ZONE', id: zone.id } : null;
  };

  const getZoneHandles = (rects: ZoneRect[]) => {
    const bounds = getRectsBounds(rects);
    if (!bounds) return [];
    const midX = bounds.x + bounds.w / 2; const midY = bounds.y + bounds.h / 2;
    return [
      { handle: 'nw' as const, x: bounds.x, y: bounds.y }, { handle: 'n' as const, x: midX, y: bounds.y },
      { handle: 'ne' as const, x: bounds.x + bounds.w, y: bounds.y }, { handle: 'e' as const, x: bounds.x + bounds.w, y: midY },
      { handle: 'se' as const, x: bounds.x + bounds.w, y: bounds.y + bounds.h }, { handle: 's' as const, x: midX, y: bounds.y + bounds.h },
      { handle: 'sw' as const, x: bounds.x, y: bounds.y + bounds.h }, { handle: 'w' as const, x: bounds.x, y: midY }
    ];
  };

  const snapPoint = (point: { x: number; y: number }, excludedNodeId?: string): { point: { x: number; y: number }; nodeId?: string } => {
    if (shiftPressed) return { point };
    const threshold = 12 / currentScale();
    const candidates: Array<{ point: { x: number; y: number }; nodeId?: string }> = [
      ...nodes.filter(node => node.id !== excludedNodeId).map(node => ({ point: { x: node.x, y: node.y }, nodeId: node.id })),
      ...zones.flatMap(zone => {
        const center = getZoneCenter(zone);
        return [
          { point: center },
          ...zone.rects.flatMap(rect => [
            { point: { x: rect.x, y: rect.y } }, { point: { x: rect.x + rect.w, y: rect.y } },
            { point: { x: rect.x + rect.w, y: rect.y + rect.h } }, { point: { x: rect.x, y: rect.y + rect.h } }
          ])
        ];
      })
    ];
    const closest = candidates.reduce<(typeof candidates)[number] | null>((best, candidate) => {
      const distance = Math.hypot(candidate.point.x - point.x, candidate.point.y - point.y);
      if (distance > threshold) return best;
      return !best || distance < Math.hypot(best.point.x - point.x, best.point.y - point.y) ? candidate : best;
    }, null);
    return closest ? { point: closest.point, nodeId: closest.nodeId } : { point };
  };

  const calculateMoveDelta = (interaction: Extract<EditorInteraction, { type: 'MOVE' }>) => {
    let dx = interaction.current.x - interaction.start.x;
    let dy = interaction.current.y - interaction.start.y;
    const bounds = getSelectionBounds(state, interaction.items);
    if (bounds && !shiftPressed) {
      const selected = new Set(interaction.items.map(item => `${item.kind}:${item.id}`));
      const xTargets = [
        ...nodes.filter(node => !selected.has(`NODE:${node.id}`)).map(node => node.x),
        ...zones.filter(zone => !selected.has(`ZONE:${zone.id}`)).flatMap(zone => {
          const box = getRectsBounds(zone.rects); return box ? [box.x, box.x + box.w / 2, box.x + box.w] : [];
        })
      ];
      const yTargets = [
        ...nodes.filter(node => !selected.has(`NODE:${node.id}`)).map(node => node.y),
        ...zones.filter(zone => !selected.has(`ZONE:${zone.id}`)).flatMap(zone => {
          const box = getRectsBounds(zone.rects); return box ? [box.y, box.y + box.h / 2, box.y + box.h] : [];
        })
      ];
      const threshold = 12 / currentScale();
      const xAnchors = [bounds.x + dx, bounds.x + bounds.w / 2 + dx, bounds.x + bounds.w + dx];
      const yAnchors = [bounds.y + dy, bounds.y + bounds.h / 2 + dy, bounds.y + bounds.h + dy];
      const xCorrection = xTargets.flatMap(target => xAnchors.map(anchor => target - anchor)).sort((a, b) => Math.abs(a) - Math.abs(b))[0];
      const yCorrection = yTargets.flatMap(target => yAnchors.map(anchor => target - anchor)).sort((a, b) => Math.abs(a) - Math.abs(b))[0];
      if (xCorrection !== undefined && Math.abs(xCorrection) <= threshold) dx += xCorrection;
      if (yCorrection !== undefined && Math.abs(yCorrection) <= threshold) dy += yCorrection;
    }
    return clampSelectionDelta(bounds, dx, dy, activeWidth, activeHeight);
  };


  const handleMouseDown = (e: MouseEvent) => {
    if (getCanvasPointerIntent({ button: e.button, editMode, spacePressed, shiftPressed: e.shiftKey, hasEditableTarget: true }) === 'PAN') {
      e.preventDefault();
      return;
    }
    handleInteractionStart(e);
    const pos = getMouseWorldPos(e);
    if (!pos) return;

    if (editMode === 'DRAW_PATH' || editMode === 'DRAW_ARCH_PATH') {
      let currentPathId = selectedPathId;
      if (!currentPathId) {
        const newId = Math.random().toString(36).substr(2, 9);
        dispatch({ type: 'ADD_PATH', payload: {
          id: newId,
          name: editMode === 'DRAW_ARCH_PATH' ? `Pac. kelias ${paths.length + 1}` : `Sim. ryšys ${paths.length + 1}`,
          kind: editMode === 'DRAW_ARCH_PATH' ? 'ARCHITECTURAL' : 'SIMULATION',
          points: [], nodeIds: [], generatedNodeIds: [], connections: [], color: '#06b6d4', visible: true
        } });
        currentPathId = newId;
      }
      const pointToAdd = snapTarget && !shiftPressed ? { x: snapTarget.x, y: snapTarget.y } : pos;
      
      if (editMode === 'DRAW_PATH') {
        let targetNode = nodes.find(n => n.x === pointToAdd.x && n.y === pointToAdd.y);
        let generatedNode: Node | undefined;
        if (!targetNode) {
          const newNodeId = Math.random().toString(36).substr(2, 9);
          const nodeType = (snapTarget?.type === 'ZONE' ? snapTarget.zoneType : NodeType.WAYPOINT) as NodeType;
          targetNode = createBlueprintNode(newNodeId, pointToAdd.x, pointToAdd.y, nodeType);
          generatedNode = targetNode;
        }
        dispatch({ type: 'ADD_POINT_TO_PATH', payload: { pathId: currentPathId, point: pointToAdd, nodeId: targetNode.id, generatedNode } });
      } else {
        dispatch({ type: 'ADD_POINT_TO_PATH', payload: { pathId: currentPathId, point: pointToAdd } });
      }
      return;
    }

    if (editMode === 'EDIT_PATH' && selectedPathId) {
      e.stopPropagation();
      const path = paths.find(item => item.id === selectedPathId);
      const threshold = 10 / currentScale();
      const pointIndex = path?.points.findIndex(point => Math.hypot(point.x - pos.x, point.y - pos.y) <= threshold) ?? -1;
      if (path && pointIndex >= 0) {
        selectionHandledRef.current = true;
        dispatch({ type: 'SELECT_PATH_POINT', payload: { pathId: path.id, index: pointIndex } });
        setEditorInteraction({ type: 'PATH_POINT', start: pos, current: pos, pathId: path.id, index: pointIndex });
      }
      return;
    }

    if (editMode === 'SELECT') {
      e.stopPropagation();
      const clickedAgent = agents.find(agent => Math.hypot(agent.x - pos.x, agent.y - pos.y) <= 12 / currentScale());
      if (clickedAgent) {
        selectionHandledRef.current = true;
        dispatch({ type: 'SELECT_AGENT', payload: clickedAgent.id });
        dispatch({ type: 'SET_SELECTION', payload: [] });
        return;
      }
      const singleZone = selectedItems.length === 1 && selectedItems[0].kind === 'ZONE'
        ? zones.find(zone => zone.id === selectedItems[0].id)
        : null;
      const resizeHandle = singleZone && getZoneHandles(singleZone.rects).find(handle => Math.hypot(handle.x - pos.x, handle.y - pos.y) <= 10 / currentScale());
      if (singleZone && resizeHandle) {
        selectionHandledRef.current = true;
        dispatch({ type: 'SELECT_AGENT', payload: null });
        setEditorInteraction({ type: 'RESIZE_ZONE', start: pos, current: pos, zoneId: singleZone.id, rects: singleZone.rects, handle: resizeHandle.handle });
        return;
      }

      const hit = findEditableAt(pos);
      if (hit) {
        selectionHandledRef.current = true;
        dispatch({ type: 'SELECT_AGENT', payload: null });
        const alreadySelected = selectedItems.some(item => item.kind === hit.kind && item.id === hit.id);
        if (!e.shiftKey && !alreadySelected) dispatch({ type: 'SET_SELECTION', payload: [hit] });
        const dragItems = e.shiftKey
          ? (alreadySelected ? selectedItems : [...selectedItems, hit])
          : (alreadySelected ? selectedItems : [hit]);
        setEditorInteraction({ type: 'MOVE', start: pos, current: pos, items: dragItems, toggleOnClick: e.shiftKey ? hit : undefined });
      } else {
        dispatch({ type: 'SELECT_AGENT', payload: null });
        const pointerIntent = getCanvasPointerIntent({
          button: e.button,
          editMode,
          spacePressed,
          shiftPressed: e.shiftKey,
          hasEditableTarget: false
        });
        if (pointerIntent === 'MARQUEE') {
          e.stopPropagation();
          selectionHandledRef.current = true;
          setEditorInteraction({ type: 'MARQUEE', start: pos, current: pos, additive: true });
        } else {
          selectionHandledRef.current = true;
          dispatch({ type: 'SET_SELECTION', payload: [] });
        }
      }
    }
  };

  const handleEditorMouseMove = (e: MouseEvent) => {
    const pos = getMouseWorldPos(e);
    if (pos) {
      setCursorPos(pos);
      if (editorInteraction) setEditorInteraction({ ...editorInteraction, current: pos });
    }
  };

  const handleMouseUp = (e: MouseEvent) => {
    const pos = getMouseWorldPos(e);
    if (editorInteraction && pos) {
      const distance = Math.hypot(pos.x - editorInteraction.start.x, pos.y - editorInteraction.start.y) * currentScale();
      if (editorInteraction.type === 'MOVE' && distance >= 4) {
        if (editorInteraction.toggleOnClick) dispatch({ type: 'SET_SELECTION', payload: editorInteraction.items });
        const delta = calculateMoveDelta({ ...editorInteraction, current: pos });
        dispatch({ type: 'TRANSFORM_ITEMS', payload: { items: editorInteraction.items, ...delta } });
      } else if (editorInteraction.type === 'MOVE' && editorInteraction.toggleOnClick) {
        dispatch({ type: 'TOGGLE_SELECTION', payload: editorInteraction.toggleOnClick });
      } else if (editorInteraction.type === 'MARQUEE' && distance >= 4) {
        const rect = {
          x: Math.min(editorInteraction.start.x, pos.x), y: Math.min(editorInteraction.start.y, pos.y),
          w: Math.abs(pos.x - editorInteraction.start.x), h: Math.abs(pos.y - editorInteraction.start.y)
        };
        const found = selectItemsInRect(state, rect);
        if (editorInteraction.additive) {
          const map = new Map([...selectedItems, ...found].map(item => [`${item.kind}:${item.id}`, item]));
          dispatch({ type: 'SET_SELECTION', payload: [...map.values()] });
        } else dispatch({ type: 'SET_SELECTION', payload: found });
      } else if (editorInteraction.type === 'RESIZE_ZONE' && distance >= 4) {
        let rects = resizeZoneRects(
          editorInteraction.rects,
          editorInteraction.handle,
          pos.x - editorInteraction.start.x,
          pos.y - editorInteraction.start.y,
          e.shiftKey
        );
        const bounds = getRectsBounds(rects);
        if (bounds) {
          const correction = clampSelectionDelta(bounds, 0, 0, activeWidth, activeHeight);
          rects = rects.map(rect => ({ ...rect, x: rect.x + correction.dx, y: rect.y + correction.dy }));
        }
        dispatch({ type: 'RESIZE_ZONE', payload: { id: editorInteraction.zoneId, rects } });
      } else if (editorInteraction.type === 'PATH_POINT' && distance >= 4) {
        const path = paths.find(item => item.id === editorInteraction.pathId);
        const oldNodeId = path?.nodeIds?.[editorInteraction.index] ?? undefined;
        const snapped = snapPoint(pos, oldNodeId);
        dispatch({ type: 'MOVE_PATH_POINT', payload: {
          pathId: editorInteraction.pathId, index: editorInteraction.index, point: snapped.point, snapNodeId: snapped.nodeId
        } });
      }
      setEditorInteraction(null);
      return;
    }
    if ((editMode === 'ADD_ZONE' || editMode === 'REGION_DELETE') && zoneStart && pos) {
      const x = Math.min(zoneStart.x, pos.x), y = Math.min(zoneStart.y, pos.y), w = Math.abs(pos.x - zoneStart.x), h = Math.abs(pos.y - zoneStart.y);
      if (w > 5 && h > 5) {
        if (editMode === 'ADD_ZONE') {
          const newZoneId = Math.random().toString(36).substr(2,9);
          dispatch({ type: 'ADD_ZONE', payload: { id: newZoneId, type: state.selectedNodeType, rects: [{ x, y, w, h }] } });
          dispatch({ type: 'SET_SELECTION', payload: [{ kind: 'ZONE', id: newZoneId }] });
        }
        else dispatch({ type: 'REGION_DELETE', payload: { x, y, w, h } });
      }
      setZoneStart(null);
    }
  };

  const handleCanvasClick = (e: MouseEvent) => {
    if (selectionHandledRef.current) {
      selectionHandledRef.current = false;
      return;
    }
    const pos = getMouseWorldPos(e);
    if (!pos || editMode === 'DRAW_PATH' || editMode === 'DRAW_ARCH_PATH') return;
    
    if (editMode === 'SELECT') {
        const clickedAgent = agents.find(agent => Math.hypot(agent.x - pos.x, agent.y - pos.y) <= 12);
        if (clickedAgent) {
          dispatch({ type: 'SELECT_AGENT', payload: clickedAgent.id });
          dispatch({ type: 'SET_SELECTION', payload: [] });
          return;
        }
        const clickedNode = nodes.find(n => Math.sqrt(Math.pow(n.x - pos.x, 2) + Math.pow(n.y - pos.y, 2)) <= 15);
        if (clickedNode) {
          dispatch({ type: 'SET_SELECTION', payload: [{ kind: 'NODE', id: clickedNode.id }] });
          dispatch({ type: 'SELECT_AGENT', payload: null });
          return;
        }
        const clickedIcon = icons.find(icon => Math.hypot(icon.x - pos.x, icon.y - pos.y) <= 16);
        if (clickedIcon) {
          dispatch({ type: 'SET_SELECTION', payload: [{ kind: 'ICON', id: clickedIcon.id }] });
          dispatch({ type: 'SELECT_AGENT', payload: null });
          return;
        }
        const clickedPath = [...paths].reverse().find(path => path.visible && path.points.some((point, index) =>
          index > 0 && distanceToSegment(pos, path.points[index - 1], point) <= 8
        ));
        if (clickedPath) {
          dispatch({ type: 'SET_SELECTION', payload: [{ kind: 'PATH', id: clickedPath.id }] });
          dispatch({ type: 'SELECT_AGENT', payload: null });
          return;
        }
        const clickedZone = [...zones].reverse().find(z => (z.rects || []).some(r => pos.x >= r.x && pos.x <= r.x + r.w && pos.y >= r.y && pos.y <= r.y + r.h));
        dispatch({ type: 'SET_SELECTION', payload: clickedZone ? [{ kind: 'ZONE', id: clickedZone.id }] : [] });
        dispatch({ type: 'SELECT_AGENT', payload: null });
    } else if (editMode === 'ADD_NODE') {
        const id = Math.random().toString(36).substr(2,9);
        dispatch({ type: 'ADD_NODE', payload: createBlueprintNode(id, pos.x, pos.y, state.selectedNodeType) });
    } else if (editMode === 'ADD_ICON') {
        const iconMeta = ICON_METADATA[state.selectedIconType];
        dispatch({ 
            type: 'ADD_ICON', 
            payload: { 
                id: Math.random().toString(36).substr(2,9), 
                x: pos.x, 
                y: pos.y, 
                type: state.selectedIconType, 
                color: iconMeta?.color || '#ffffff',
                label: iconMeta?.label || '' 
            } 
        });
    } else if (editMode === 'DRAW_WALL') {
        if (!wallStart) setWallStart(pos);
        else {
          if (Math.hypot(pos.x - wallStart.x, pos.y - wallStart.y) > 5) {
            dispatch({ type: 'ADD_WALL', payload: { id: Math.random().toString(36).substr(2, 9), x1: wallStart.x, y1: wallStart.y, x2: pos.x, y2: pos.y } });
          }
          setWallStart(null);
        }
    } else if (editMode === 'DELETE') {
        const clickedNode = nodes.find(n => Math.sqrt(Math.pow(n.x - pos.x, 2) + Math.pow(n.y - pos.y, 2)) <= 15);
        if (clickedNode) {
          dispatch({ type: 'DELETE_NODE', payload: clickedNode.id });
          return;
        }
        const clickedIcon = icons.find(icon => Math.sqrt(Math.pow(icon.x - pos.x, 2) + Math.pow(icon.y - pos.y, 2)) <= 15);
        if (clickedIcon) {
          dispatch({ type: 'DELETE_ICON', payload: clickedIcon.id });
          return;
        }
        const clickedPath = [...paths].reverse().find(path => path.visible && path.points.some((point, index) =>
          index > 0 && distanceToSegment(pos, path.points[index - 1], point) <= 8
        ));
        if (clickedPath) {
          dispatch({ type: 'DELETE_PATH', payload: clickedPath.id });
          return;
        }
        const clickedWall = [...walls].reverse().find(wall => distanceToSegment(pos, { x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 }) <= 8);
        if (clickedWall) {
          dispatch({ type: 'DELETE_WALL', payload: clickedWall.id });
          return;
        }
        const clickedZone = zones.find(z => (z.rects || []).some(r => pos.x >= r.x && pos.x <= r.x + r.w && pos.y >= r.y && pos.y <= r.y + r.h));
        if (clickedZone) dispatch({ type: 'DELETE_ZONE', payload: clickedZone.id });
    } else if (editMode === 'CALIBRATE_SCALE') {
        dispatch({ type: 'SET_CALIBRATION', payload: { points: state.calibrationPoints.length >= 2 ? [pos] : [...state.calibrationPoints, pos] } });
    } else if (editMode === 'MEASURE_PATH') {
        dispatch({ type: 'SET_MEASUREMENT_POINTS', payload: measurementPoints.length >= 2 ? [pos] : [...measurementPoints, pos] });
    }
  };

  const handleCanvasDoubleClick = (e: MouseEvent) => {
    if (editMode !== 'EDIT_PATH' || !selectedPathId) return;
    const pos = getMouseWorldPos(e);
    const path = paths.find(item => item.id === selectedPathId);
    if (!pos || !path || path.points.length < 2) return;
    let bestIndex = -1; let bestDistance = Infinity; let projected = pos;
    path.points.forEach((point, index) => {
      if (index === 0) return;
      const start = path.points[index - 1];
      const lengthSquared = Math.pow(point.x - start.x, 2) + Math.pow(point.y - start.y, 2);
      const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((pos.x - start.x) * (point.x - start.x) + (pos.y - start.y) * (point.y - start.y)) / lengthSquared));
      const candidate = { x: start.x + t * (point.x - start.x), y: start.y + t * (point.y - start.y) };
      const distance = Math.hypot(candidate.x - pos.x, candidate.y - pos.y);
      if (distance < bestDistance) { bestDistance = distance; bestIndex = index; projected = candidate; }
    });
    if (bestIndex < 0 || bestDistance > 12 / currentScale()) return;
    const snapped = snapPoint(projected);
    const isSimulation = (path.kind ?? (path.name.toLowerCase().startsWith('sim.') ? 'SIMULATION' : 'ARCHITECTURAL')) === 'SIMULATION';
    const generatedNode = isSimulation && !snapped.nodeId
      ? createBlueprintNode(Math.random().toString(36).slice(2, 11), snapped.point.x, snapped.point.y, NodeType.WAYPOINT)
      : undefined;
    dispatch({ type: 'INSERT_PATH_POINT', payload: {
      pathId: path.id, index: bestIndex, point: snapped.point, nodeId: snapped.nodeId, generatedNode
    } });
  };

  let displayNodes = nodes;
  let displayZones = zones;
  let displayWalls = walls;
  let displayPaths = paths;
  let displayIcons = icons;
  if (editorInteraction?.type === 'MOVE' && Math.hypot(editorInteraction.current.x - editorInteraction.start.x, editorInteraction.current.y - editorInteraction.start.y) * currentScale() >= 4) {
    const { dx, dy } = calculateMoveDelta(editorInteraction);
    const selected = new Set(editorInteraction.items.map(item => `${item.kind}:${item.id}`));
    const selectedPathIds = new Set(editorInteraction.items.filter(item => item.kind === 'PATH').map(item => item.id));
    const movedNodeIds = new Set(editorInteraction.items.filter(item => item.kind === 'NODE').map(item => item.id));
    paths.filter(path => selectedPathIds.has(path.id)).forEach(path => path.nodeIds?.forEach(id => { if (id) movedNodeIds.add(id); }));
    displayNodes = nodes.map(node => movedNodeIds.has(node.id) ? { ...node, x: node.x + dx, y: node.y + dy } : node);
    const movedNodeMap = new Map(displayNodes.map(node => [node.id, node]));
    displayPaths = paths.map(path => ({
      ...path,
      points: path.points.map((point, index) => {
        const nodeId = path.nodeIds?.[index];
        if (nodeId && movedNodeIds.has(nodeId)) {
          const node = movedNodeMap.get(nodeId); return node ? { x: node.x, y: node.y } : point;
        }
        return selectedPathIds.has(path.id) && !nodeId ? { x: point.x + dx, y: point.y + dy } : point;
      })
    }));
    displayZones = zones.map(zone => selected.has(`ZONE:${zone.id}`) ? { ...zone, rects: zone.rects.map(rect => ({ ...rect, x: rect.x + dx, y: rect.y + dy })) } : zone);
    displayIcons = icons.map(icon => selected.has(`ICON:${icon.id}`) ? { ...icon, x: icon.x + dx, y: icon.y + dy } : icon);
    displayWalls = walls.map(wall => selected.has(`WALL:${wall.id}`) ? { ...wall, x1: wall.x1 + dx, y1: wall.y1 + dy, x2: wall.x2 + dx, y2: wall.y2 + dy } : wall);
  } else if (editorInteraction?.type === 'RESIZE_ZONE') {
    const rects = resizeZoneRects(editorInteraction.rects, editorInteraction.handle, editorInteraction.current.x - editorInteraction.start.x, editorInteraction.current.y - editorInteraction.start.y, shiftPressed);
    displayZones = zones.map(zone => zone.id === editorInteraction.zoneId ? { ...zone, rects } : zone);
  } else if (editorInteraction?.type === 'PATH_POINT') {
    const path = paths.find(item => item.id === editorInteraction.pathId);
    const oldNodeId = path?.nodeIds?.[editorInteraction.index] ?? undefined;
    const snapped = snapPoint(editorInteraction.current, oldNodeId);
    displayNodes = oldNodeId && !snapped.nodeId ? nodes.map(node => node.id === oldNodeId ? { ...node, ...snapped.point } : node) : nodes;
    displayPaths = paths.map(item => ({
      ...item,
      points: item.points.map((point, index) => {
        if (item.id === editorInteraction.pathId && index === editorInteraction.index) return snapped.point;
        if (oldNodeId && item.nodeIds?.[index] === oldNodeId && !snapped.nodeId) return snapped.point;
        return point;
      })
    }));
  }

  const marqueeBounds: Bounds | null = editorInteraction?.type === 'MARQUEE' ? {
    x: Math.min(editorInteraction.start.x, editorInteraction.current.x),
    y: Math.min(editorInteraction.start.y, editorInteraction.current.y),
    w: Math.abs(editorInteraction.current.x - editorInteraction.start.x),
    h: Math.abs(editorInteraction.current.y - editorInteraction.start.y)
  } : null;
  const selectedZoneForHandles = selectedItems.length === 1 && selectedItems[0].kind === 'ZONE'
    ? displayZones.find(zone => zone.id === selectedItems[0].id)
    : null;

  const hoveredResizeHandle = editMode === 'SELECT' && selectedZoneForHandles && cursorPos
    ? getZoneHandles(selectedZoneForHandles.rects).find(handle => Math.hypot(handle.x - cursorPos.x, handle.y - cursorPos.y) <= 10 / currentScale())?.handle
    : undefined;
  const resizeCursor: Partial<Record<ResizeHandle, string>> = {
    n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
    ne: 'nesw-resize', sw: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize'
  };
  const canvasCursor = isPanning ? 'grabbing'
    : (spacePressed || editMode === 'PAN') ? 'grab'
    : editorInteraction?.type === 'RESIZE_ZONE' ? resizeCursor[editorInteraction.handle]
    : hoveredResizeHandle ? resizeCursor[hoveredResizeHandle]
    : editorInteraction?.type === 'MOVE' ? 'move'
    : editorInteraction?.type === 'MARQUEE' ? 'crosshair'
    : editMode === 'SELECT' ? (cursorPos && findEditableAt(cursorPos) ? 'move' : 'grab')
    : 'crosshair';

  return (
    <div ref={viewerRef} className="w-full h-full bg-slate-950 overflow-hidden relative group">
      <TransformWrapper 
        ref={transformRef} 
        initialScale={1}
        minScale={0.01} 
        maxScale={20} 
        limitToBounds={false}
        onInit={() => requestAnimationFrame(fitToView)}
        onPanningStart={() => setIsPanning(true)}
        onPanningStop={() => setIsPanning(false)}
        doubleClick={{ disabled: true }}
        panning={{
          disabled: false,
          allowLeftClickPan: editMode === 'SELECT' || editMode === 'PAN' || spacePressed,
          allowMiddleClickPan: true,
          allowRightClickPan: false
        }}
        wheel={{ step: 0.1 }}
      >
        <div className="absolute top-6 right-6 z-40 flex flex-col gap-2 mt-12" onMouseDown={event => event.stopPropagation()}>
            <button type="button" aria-label="Didinti" title="Didinti" onClick={() => zoomBy(1.25)} className="p-3 bg-slate-900/90 rounded-lg border border-slate-700 text-cyan-400 hover:bg-slate-800 transition-colors"><ZoomIn size={18}/></button>
            <button type="button" aria-label="Mažinti" title="Mažinti" onClick={() => zoomBy(0.8)} className="p-3 bg-slate-900/90 rounded-lg border border-slate-700 text-cyan-400 hover:bg-slate-800 transition-colors"><ZoomOut size={18}/></button>
            <button type="button" aria-label="Sutalpinti visą planą" title="Sutalpinti visą planą" onClick={fitToView} className="p-3 bg-slate-900/90 rounded-lg border border-slate-700 text-cyan-400 hover:bg-slate-800 transition-colors"><Maximize size={18}/></button>
        </div>

        <TransformComponent wrapperClass="w-full h-full">
          <div ref={exportAreaRef} style={{ width: activeWidth, height: activeHeight, position: 'relative', cursor: canvasCursor }}
            onMouseMove={handleEditorMouseMove}
            onAuxClick={event => { if (event.button === 1) event.preventDefault(); }}
            onClick={handleCanvasClick} onDoubleClick={handleCanvasDoubleClick} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}
          >
            {floorPlanImage && <img src={floorPlanImage} alt="planas" className="absolute top-0 left-0 pointer-events-none" style={{ width: activeWidth, height: activeHeight, opacity: mapOpacity, filter: `brightness(${mapBrightness}) contrast(${mapContrast})` }} />}
            <svg className="absolute inset-0 pointer-events-none z-20" width={activeWidth} height={activeHeight}>
              {displayWalls.map(wall => <line key={wall.id} x1={wall.x1} y1={wall.y1} x2={wall.x2} y2={wall.y2} stroke={selectedWallIds.has(wall.id) ? '#22d3ee' : '#e2e8f0'} strokeWidth={selectedWallIds.has(wall.id) ? 7 : 4} />)}
              {wallStart && editMode === 'DRAW_WALL' && cursorPos && <line x1={wallStart.x} y1={wallStart.y} x2={cursorPos.x} y2={cursorPos.y} stroke="#06b6d4" strokeWidth="3" strokeDasharray="6 4" />}
              {displayPaths.filter(p => p.visible).map(p => {
                 if (p.points.length < 2) return null;
                 let pathD = `M ${p.points[0].x} ${p.points[0].y}`;
                 for(let i=1; i<p.points.length; i++) pathD += ` L ${p.points[i].x} ${p.points[i].y}`;
                 return <path key={p.id} d={pathD} fill="none" stroke={p.color} strokeWidth={selectedItems.some(item => item.kind === 'PATH' && item.id === p.id) ? 6 : 3} opacity={selectedItems.some(item => item.kind === 'PATH' && item.id === p.id) ? 1 : 0.6} />;
              })}
              {measurementPoints.length > 0 && (
                <g>
                  {measurementPoints.length === 2 && (
                    <>
                      <line x1={measurementPoints[0].x} y1={measurementPoints[0].y} x2={measurementPoints[1].x} y2={measurementPoints[1].y} stroke="#020617" strokeWidth="9" opacity="0.9" />
                      <line x1={measurementPoints[0].x} y1={measurementPoints[0].y} x2={measurementPoints[1].x} y2={measurementPoints[1].y} stroke="#22d3ee" strokeWidth="3" strokeDasharray="8 5" />
                    </>
                  )}
                  {measurementPoints.map((point, index) => (
                    <g key={index}>
                      <circle cx={point.x} cy={point.y} r="9" fill="#020617" opacity="0.9" />
                      <circle cx={point.x} cy={point.y} r="5" fill="#22d3ee" stroke="#ffffff" strokeWidth="2" />
                    </g>
                  ))}
                  {measurementMidpoint && (
                    <g transform={`translate(${measurementMidpoint.x}, ${measurementMidpoint.y - 14})`}>
                      <rect x="-45" y="-16" width="90" height="28" rx="7" fill="#020617" stroke="#22d3ee" strokeWidth="2" opacity="0.96" />
                      <text x="0" y="2" fill="#ffffff" fontSize="14" fontWeight="700" fontFamily="JetBrains Mono, monospace" textAnchor="middle">{measurementLabel}</text>
                    </g>
                  )}
                </g>
              )}
              {displayIcons.map(icon => {
                const meta = ICON_METADATA[icon.type];
                if (!meta) return null;
                const IconComponent = meta.icon;
                return (
                  <g key={icon.id} transform={`translate(${icon.x}, ${icon.y})`}>
                    <circle r={selectedIconIds.has(icon.id) ? 17 : 14} fill="rgba(15, 23, 42, 0.9)" stroke={selectedIconIds.has(icon.id) ? '#ffffff' : meta.color} strokeWidth={selectedIconIds.has(icon.id) ? 3 : 1.5} />
                    <foreignObject x="-9" y="-9" width="18" height="18">
                      <div className="w-full h-full flex items-center justify-center">
                        <IconComponent size={14} color={meta.color} />
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
              {editMode === 'EDIT_PATH' && selectedPathId && displayPaths.find(path => path.id === selectedPathId)?.points.map((point, index) => (
                <g key={`path-handle-${index}`}>
                  <circle cx={point.x} cy={point.y} r={9 / currentScale()} fill="#020617" stroke={selectedPathPoint?.pathId === selectedPathId && selectedPathPoint.index === index ? '#ffffff' : '#22d3ee'} strokeWidth={2 / currentScale()} />
                  <circle cx={point.x} cy={point.y} r={3 / currentScale()} fill="#22d3ee" />
                </g>
              ))}
              {selectedZoneForHandles && (() => {
                const bounds = getRectsBounds(selectedZoneForHandles.rects);
                if (!bounds) return null;
                return <g>
                  <rect x={bounds.x} y={bounds.y} width={bounds.w} height={bounds.h} fill="none" stroke="#22d3ee" strokeWidth={2 / currentScale()} strokeDasharray={`${7 / currentScale()} ${4 / currentScale()}`} />
                  {getZoneHandles(selectedZoneForHandles.rects).map(handle => <rect key={handle.handle} x={handle.x - 5 / currentScale()} y={handle.y - 5 / currentScale()} width={10 / currentScale()} height={10 / currentScale()} rx={2 / currentScale()} fill="#020617" stroke="#22d3ee" strokeWidth={2 / currentScale()} />)}
                </g>;
              })()}
              {marqueeBounds && <rect x={marqueeBounds.x} y={marqueeBounds.y} width={marqueeBounds.w} height={marqueeBounds.h} fill="rgba(34,211,238,0.12)" stroke="#22d3ee" strokeWidth={2 / currentScale()} strokeDasharray={`${6 / currentScale()} ${4 / currentScale()}`} />}
            </svg>
            <StaticLayer nodes={displayNodes} zones={displayZones} width={activeWidth} height={activeHeight} pixelsPerMeter={pixelsPerMeter} selectedItems={selectedItems} hoveredZoneId={null} editMode={editMode} viewMode={viewMode} />
            <AgentLayer agents={agents} selectedAgentId={selectedAgentId} width={activeWidth} height={activeHeight} />
          </div>
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
});

export default SimulationCanvas;
